import React from "react";
import { Link } from "react-router-dom";
import "../App.css";
import SiteNav from "../components/SiteNav";
import DealerResourceSummary from "../components/DealerResourceSummary";
import usePageMeta from "../hooks/usePageMeta";
import { DEALERSHIP } from "../data/dealership";
import { SITE } from "../data/site";

const complaintLink =
  "https://www.ohioattorneygeneral.gov/individuals-and-families/consumers/file-a-complaint.aspx";

const locationTokens = ["findlay, oh", "findlay, ohio"];

const hasLocation = (value) => {
  if (!value) {
    return false;
  }
  const normalized = value.toLowerCase();
  return locationTokens.some((token) => normalized.includes(token));
};

const withLocationSuffix = (value) => {
  if (!value || hasLocation(value)) {
    return value;
  }
  return `${value} (${DEALERSHIP.location})`;
};

const withLocationSentence = (value) => {
  if (!value || hasLocation(value)) {
    return value;
  }
  return `${value} Applies to ${DEALERSHIP.name} in ${DEALERSHIP.location}.`;
};

const dealerAboutSchema = {
  "@type": "AutoDealer",
  name: DEALERSHIP.name,
  url: DEALERSHIP.website,
  telephone: DEALERSHIP.phone,
  address: {
    "@type": "PostalAddress",
    streetAddress: DEALERSHIP.streetAddress,
    addressLocality: DEALERSHIP.city,
    addressRegion: DEALERSHIP.state,
    postalCode: DEALERSHIP.postalCode,
    addressCountry: DEALERSHIP.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: DEALERSHIP.latitude,
    longitude: DEALERSHIP.longitude,
  },
  areaServed: DEALERSHIP.nearbyMarkets,
  sameAs: [DEALERSHIP.website, DEALERSHIP.bbbReviews, DEALERSHIP.yelp],
};

function TopicPage({ page, allPages }) {
  const locationScope = [
    page.title,
    page.metaTitle,
    page.description,
    page.metaDescription,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const shouldAddLocation = locationScope.includes("findlay chrysler");

  const metaTitle = shouldAddLocation
    ? withLocationSuffix(page.metaTitle || page.title)
    : page.metaTitle || page.title;
  const metaDescription = shouldAddLocation
    ? withLocationSentence(page.description || page.metaDescription)
    : page.description || page.metaDescription;

  usePageMeta({
    title: metaTitle,
    description: metaDescription,
  });

  const relatedPages = Array.isArray(allPages)
    ? allPages.filter((item) => item.slug !== page.slug)
    : [];
  const pageUrl = `${SITE.baseUrl}${page.slug}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.title,
    name: page.metaTitle || page.title,
    description: metaDescription,
    mainEntityOfPage: pageUrl,
    url: pageUrl,
    isAccessibleForFree: true,
    about: dealerAboutSchema,
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      url: SITE.baseUrl,
    },
  };

  return (
    <>
      <SiteNav />
      <div className="container">
        <div className="text-center mt-5 text-uppercase">
          <h1 className="h3">{page.title}</h1>
          {page.subheading ? <p className="lead">{page.subheading}</p> : null}
        </div>
        <div className="mt-4 text-start">
          {page.sections.map((section) => (
            <div className="mb-4" key={section.heading}>
              <h4>{section.heading}</h4>
              {section.paragraphs
                ? section.paragraphs.map((paragraph, index) => (
                    <p key={`${section.heading}-${index}`}>{paragraph}</p>
                  ))
                : null}
              {section.list && section.list.length ? (
                <ul>
                  {section.list.map((item) => (
                    <li key={`${section.heading}-${item}`}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
          <div className="container text-center d-grid mx-auto my-4">
            <a
              href={complaintLink}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary active"
              type="button"
              id="cc"
              style={{ backgroundColor: "#0377fc !important" }}
            >
              FILE A COMPLAINT
            </a>
          </div>
          <DealerResourceSummary compact />
          {page.resources && page.resources.length ? (
            <div className="mt-4">
              <h4>Resources</h4>
              <ul>
                {page.resources.map((resource) => (
                  <li key={resource.href}>
                    <a href={resource.href} target="_blank" rel="noreferrer">
                      {resource.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {page.sources && page.sources.length ? (
            <div className="mt-4">
              <h4>Sources</h4>
              <ul>
                {page.sources.map((source) => (
                  <li key={source.href}>
                    <a href={source.href} target="_blank" rel="noreferrer">
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {relatedPages.length ? (
            <div className="mt-4">
              <h4>Related Pages</h4>
              <ul>
                {relatedPages.map((related) => (
                  <li key={related.slug}>
                    <Link to={related.slug}>{related.linkTitle || related.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="mt-4">
            <p className="small text-muted">
              This page summarizes publicly reported allegations and consumer
              complaints. It is not legal advice and does not assert wrongdoing.
            </p>
          </div>
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </>
  );
}

export default TopicPage;
